<template>
    <div>
        <component-loader v-if="isLoading" />
        <div class="plant-page-wrapper" v-else>
            <div v-if="plantInfo" class="plant-details">
                <div class="plant-name">
                    <h1>{{ plantDisplayValue }}</h1>
                    <div class="flex-row-with-gap">
                        <icofont-icon v-if="plantInfo.fuelIcon" size="extra-large" :icon="plantInfo.fuelIcon" background-colour="var(--sparkOrange)" />
                        <div class="flag">
                            <country-flag :iso-code="'ie'" />
                        </div>
                    </div>
                </div>
                <div class="plant-types">
                    <metric-with-value v-if="plantInfo.fuel" metric="Fuel" :value="plantInfo.fuel" />
                    <metric-with-value v-if="plantInfo.registeredCapacity !== null" metric="Registered Capacity" :value="`${plantInfo.registeredCapacity} MW`" />
                    <metric-with-value v-if="plantInfo.partyName" metric="Owner" :value="plantInfo.partyName" />
                    <metric-with-value v-if="plantInfo.unitType" metric="Unit Type" :value="plantInfo.unitType" />
                    <metric-with-value v-if="plantInfo.status" metric="Status" :value="plantInfo.status" />
                    <metric-with-value v-if="registrationDate" metric="Registration Date" :value="registrationDate" />

                    <metric-with-value v-if="plantInfo.isPriorityDispatch !== null" metric="Is Priority Dispatch" :value="plantInfo.isPriorityDispatch ? 'Yes' : 'No'" />
                    <metric-with-value v-if="plantInfo.isDispatchable !== null" metric="Is Dispatchable" :value="plantInfo.isDispatchable ? 'Yes' : 'No'" />
                    <metric-with-value v-if="plantInfo.isControllable !== null" metric="Is Controllable" :value="plantInfo.isControllable ? 'Yes' : 'No'" />
                    <metric-with-value v-if="plantInfo.isPumpStorage !== null" metric="Is Pump Storage" :value="plantInfo.isPumpStorage ? 'Yes' : 'No'" />
                    <metric-with-value v-if="plantInfo.isEnergyLimit !== null" metric="Is Energy Limit" :value="plantInfo.isEnergyLimit ? 'Yes' : 'No'" />
                    <metric-with-value v-if="plantInfo.isUnderTest !== null" metric="Is Under Test" :value="plantInfo.isUnderTest ? 'Yes' : 'No'" />
                    <metric-with-value v-if="plantInfo.isDualFuel && plantInfo.secondaryFuel" metric="Secondary Fuel" :value="plantInfo.secondaryFuel" />
                </div>
            </div>
            <div class="chart-wrapper" v-if="configs">
                <div
                    class="chart"
                    v-for="config in configs"
                    :key="config.info.id"
                >
                    <module-chart-with-options
                        :chart-config="config"
                        :title="config.info.name ?? ''"
                        :blocked-actions="NON_USER_DASHBOARD_BLOCKED_ACTIONS"
                        :date-override="props.dateOverride"
                        is-modal
                        display-options-top
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { IModuleChartConfig } from '@/interfaces/powerMarkets/charts/IModuleChartConfig.ts';
import { IPlantInfo } from '@/interfaces/powerMarkets/plant/IPlantInfo.ts';
import { plantApi } from '@/api/powerMarkets/plantApi.ts';
import { chartApi } from '@/api/powerMarkets/chartApi.ts';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import ModuleChartWithOptions from '@/components/powerMarkets/chart/ModuleChartWithOptions.vue';
import { NON_USER_DASHBOARD_BLOCKED_ACTIONS } from '@/interfaces/actions/enact/IModuleActions.ts';
import dayjs from 'dayjs';
import MetricWithValue from '@/components/common/MetricWithValue.vue';
import ComponentLoader from '@/components/layout/ComponentLoader.vue';
import IcofontIcon from '@/components/powerMarkets/common/IcofontIcon.vue';
import { IDashboardOverrides } from '@/interfaces/powerMarkets/dashboard/IDashboard.ts';
import { getPlantChartDate } from './plantChartDate.ts';

const dataStore = useDataStore();

const props = defineProps<{
    plantId: string;
    fromDate?: Date;
    toDate?: Date;
    dateOverride?: IDashboardOverrides;
}>();

const plantDisplayValue = computed<string>(() => dataStore.getPlantNameById(props.plantId));

const configs = ref<Array<IModuleChartConfig>>([]);
const plantInfo = ref<IPlantInfo | null>(null);
const isLoading = ref<boolean>(false);
const chartsToGet = ['generation', 'semoAcceptedBoaVolume', 'plantPriceVolumeDistribution', 'semoBidPrices', 'semoOfferPrices', 'semoAccessQuantity',
    'semoOperatingCosts', 'semoShortTermMaximisationCap', 'semoMinMaxOnTime', 'semoMinimumOffTime'];

const registrationDate = computed<string | null>(() => {
    if (!plantInfo.value?.registrationDate) return null;
    return dayjs(plantInfo.value.registrationDate).format('DD/MM/YYYY');
});

async function getPageData (): Promise<void> {
    await plantApi.getSemoPlantData(props.plantId).then((response) => {
        if (response.data) plantInfo.value = response.data;
    });

    const chartPayload = chartsToGet.map((chart) => ({ plantId: props.plantId, chartType: chart, country: 'Ireland' }));

    await chartApi.getPlantChartSeriesConfig(chartPayload).then((response) => {
        const chartConfigs = response;

        configs.value = chartConfigs.map((config) => ({
            ...config,
            info: {
                ...config.info,
                name: config.info.name ? config.info.name.split(' - ')[0] : '',
                date: getPlantChartDate(config, props.fromDate, props.toDate),
            },
        }));
    });
}

async function initialise (): Promise<void> {
    isLoading.value = true;
    await getPageData();
    isLoading.value = false;
}

initialise();
</script>

<style lang="scss" scoped>
@use '/src/assets/scss/components/powerMarkets/europePlants.scss';
</style>
